import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser-1p';
import { type MailboxInfo, getIndexerValueForMailboxInfo } from 'owa-client-types';
import { getAccountScopeUserSettings } from 'owa-session-store/lib/selectors/getAccountScopeUserSettings';
import { setIsAuthenticated } from '../isAnonymousSession';
import { setOwaAppId } from 'owa-config/lib/getOwaAppId';
import { logStartUsage } from 'owa-analytics-start';
import type { CustomDataMap } from 'owa-analytics-types';
import { getLoginHint } from './getLoginHint';
import { setItem } from 'owa-local-storage';
import { setIdleSessionCookieIfEnabled } from './setIdleSessionCookieIfEnabled';
import { AccountSourceType } from 'owa-account-source-list-types';
import { getTypeHintFromAccountInfo } from '../getTypeHint';
import { getRoutingHintForMsalAccount } from '../getAnchorMailbox';
import { clearMarketingRedirectSid } from './marketingRedirectSid';

const mailboxInfoIndexerValue_to_homeAccountId = new Map<string, string>();

export function getAccountFromMsal(
    msalInstance: IPublicClientApplication,
    mailboxInfo: MailboxInfo,
    isNaa: boolean = false
): AccountInfo | null {
    let account: AccountInfo | null = null;
    const diagnosticData: CustomDataMap = { isNaa };

    const key = getIndexerValueForMailboxInfo(mailboxInfo);
    let homeAccountId = mailboxInfoIndexerValue_to_homeAccountId.get(key);
    diagnosticData.hasMailboxInfoIndexerValue = !!key;
    diagnosticData.homeAccountIdFoundInCache = !!homeAccountId;

    const sessionData = getAccountScopeUserSettings(mailboxInfo).SessionSettings;
    diagnosticData.hasSessionSettings = !!sessionData;

    if (!homeAccountId && sessionData) {
        if (sessionData.ExternalDirectoryUserGuid && sessionData.ExternalDirectoryTenantGuid) {
            homeAccountId = `${sessionData.ExternalDirectoryUserGuid}.${sessionData.ExternalDirectoryTenantGuid}`;
        }

        diagnosticData.hasExternalDirectoryUserGuid = !!sessionData.ExternalDirectoryUserGuid;
        diagnosticData.hasExternalDirectoryTenantGuid = !!sessionData.ExternalDirectoryTenantGuid;
    }

    if (homeAccountId) {
        account = msalInstance.getAccount({ homeAccountId });
        diagnosticData.getAccountByHomeAccountIdResult = account ? 'Success' : 'Failure';
    }

    if (!account) {
        const loginHint = getLoginHint(undefined /*msalAccount*/, mailboxInfo);
        diagnosticData.hasLoginHint = !!loginHint;

        if (loginHint) {
            account = msalInstance.getAccount({ loginHint });

            if (account && !account.idToken) {
                // [MSAL bug workaround] msal-browser ignores the loginHint filter if the account in cache does not
                // have an idToken. In that case, it returns the first account in cache. So, if the returned account
                // doesn't have an idToken, we cannot consider this a reliable account match.
                diagnosticData.loginHintAccountMissingIdToken = true;
                account = null;
            }

            diagnosticData.getAccountByLoginHintResult = account ? 'Success' : 'Failure';
        }
    }

    // Fall back to the active account when filter-based lookups fail.
    // OWA does not support multi-account, so the active account is always the correct one.
    // Collect cache diagnostics here so every fallback event carries data about whether the
    // account existed in cache but MSAL's filter-based lookup failed to find it.
    if (!account) {
        const allAccounts = msalInstance.getAllAccounts();
        diagnosticData.totalMsalAccountsInCache = allAccounts.length;

        if (allAccounts.length > 0) {
            const loginHint = getLoginHint(undefined, mailboxInfo);
            diagnosticData.homeAccountIdCaseMismatch =
                !!homeAccountId &&
                allAccounts.some(
                    a => a.homeAccountId.toLowerCase() === homeAccountId.toLowerCase()
                );
            diagnosticData.loginHintMatchInCache =
                !!loginHint &&
                allAccounts.some(
                    a =>
                        a.username === loginHint ||
                        a.loginHint === loginHint ||
                        a.idTokenClaims?.login_hint === loginHint ||
                        a.idTokenClaims?.preferred_username === loginHint
                );
            diagnosticData.cachedAccountsWithIdToken = allAccounts.filter(a => !!a.idToken).length;
        }

        account = msalInstance.getActiveAccount();
        diagnosticData.getActiveAccountResult = account ? 'Success' : 'Failure';
        logStartUsage('MsalGetAccountActiveAccountFallback', mailboxInfo, diagnosticData);
    }

    if (key && account) {
        mailboxInfoIndexerValue_to_homeAccountId.set(key, account.homeAccountId);
    }

    return account;
}

export function getAccountFromMsalByLoginHint(
    msalInstance: IPublicClientApplication,
    loginHint: string
): AccountInfo | null {
    return msalInstance.getAccount({ loginHint });
}

export function getAllAccountFromMsal(msalInstance: IPublicClientApplication): AccountInfo[] {
    return msalInstance.getAllAccounts();
}

export function getActiveAccountFromMsal(
    msalInstance: IPublicClientApplication
): AccountInfo | null {
    return msalInstance.getActiveAccount();
}

export function setActiveAccountFromMsal(
    msalInstance: IPublicClientApplication,
    account: AccountInfo
): void {
    msalInstance.setActiveAccount(account);
}

export function setActiveAccountRelatedProperties(
    msalInstance: IPublicClientApplication,
    account: AccountInfo
): void {
    // store the app id from msal config in local token storage. This will be used to sync the app id between the client and server.
    setOwaAppId(msalInstance.getConfiguration().auth.clientId);
    setActiveAccountFromMsal(msalInstance, account);
    setItem(self, 'login_hint', account?.idTokenClaims?.login_hint ?? account?.username); // store login_hint claim value or upn in persistent storage for SSO.
    setDefaultAnchorMailboxCookie(account);

    // extract the domain hint from the username using the domain after the @
    const domainHint = account?.username.includes('@') ? account.username.split('@')[1] : '';
    const isConsumerTenant = getTypeHintFromAccountInfo(account) == AccountSourceType.OutlookDotCom;
    if (domainHint && !isConsumerTenant) {
        setItem(self, 'domain_hint', domainHint);
    }

    // most apps that rely on Anonymous sessions like FindTime, Bookings & BookWithMe
    // need this to determine whether a session is authenticated or not.
    setIsAuthenticated();

    // Clear the marketing redirect session ID on successful auth to break any potential login loop.
    clearMarketingRedirectSid();

    setIdleSessionCookieIfEnabled(account);
}

export function setMailboxInfoToAccountMapping(
    mailboxInfo: MailboxInfo,
    account: AccountInfo
): void {
    const key = getIndexerValueForMailboxInfo(mailboxInfo);
    if (key) {
        mailboxInfoIndexerValue_to_homeAccountId.set(key, account.homeAccountId);
    }
}

export function setDefaultAnchorMailboxCookie(account: AccountInfo): void {
    // A routing hint needs to be available for the index page navigation requests that will not have extra headers.
    // TODO: remove when X-AnchorMailbox header gets appedened to all OWA B2 requests. WI: https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/337215
    const DAM = getRoutingHintForMsalAccount(account);
    const signInState = account?.idTokenClaims?.signin_state;
    const isKmsiEnabled = signInState != undefined && (signInState as string[]).includes('kmsi');

    // Make the cookie persistent if KMSI is enabled, session cookie otherwise.
    if (isKmsiEnabled) {
        const expiryDate = new Date();
        expiryDate.setTime(expiryDate.getTime() + 24 * 60 * 60 * 1000); // 24 hours, matching the refresh token expiry
        self.document.cookie = `DefaultAnchorMailbox=${DAM}; path=/; secure; Samesite=none; expires=${expiryDate.toUTCString()}`;
    } else {
        self.document.cookie = `DefaultAnchorMailbox=${DAM}; path=/; secure; Samesite=none`;
    }
}
